import React, { useCallback, useMemo, useState } from 'react';
import cloneDeep from 'lodash.clonedeep';

import CustomerDropdown from 'components/base/CustomerDropdown/index';
import Box from 'components/elements/Box';
import Button from 'components/elements/button';
import createNewSubscription from 'components/notifications/Settings/utils/createNewSubscription';
import ChannelDropdown from './ChannelDropdown';
import NotifierBanner from './NotifierBanner';
import LocationDropdown, { Region } from './LocationDropdown';
import Checkbox from './Checkbox';
import P from '../../elements/p';
import Span from 'components/elements/span';
import { ReactComponent as Close } from '../../../icons/icon-close.svg';
import { ReactComponent as Chevron } from 'icons/icon-chevron.svg';

import useMedia from 'hooks/useMedia';
import useSettings from './utils/useSettings';
import useCheckboxes from './utils/useCheckboxes';
import sortByDisplayName from 'helpers/sortByDisplayName';
import addLabel from 'helpers/addLabel';
import getSelectedEventCategory from './utils/getSelectedEventCategory';
import { parsePriorityTypes } from './utils/helpers';

import {
  ChannelTypes,
  EventCategories,
  IEventChannel,
  ISubscription,
} from 'redux/reducers/subscriptions';

import { CloseButton } from './styles';
import ButtonNext from '../../elements/button';

import { ILabelValuePair, TStateSetter, ICountryFlagProps } from 'types/shared';
import {
  channelTypeNames,
  channelPriorityAndChartsTypeNames,
  eventCategoryLabels,
  eventCategoryTooltipTexts,
} from './typeToName';

import colors from 'styles/themes/colors';

import { SHOW_BELL_NOTIFICATION } from 'config';

interface ICreateSubscriptionProps {
  subscriptions?: ISubscription[];
  customers: any[];
  countries: any[];
  entityId: string;
  setIsAddingNewNotification: TStateSetter<boolean>;
  isAddingNewNotification: boolean;
  setIsShowingNotifierBanner: TStateSetter<boolean>;
  isShowingNotifierBanner: boolean;
  setIsCreatingMobileAlert: TStateSetter<boolean>;
  loading: boolean;
  regions: Region[];
  recentRegions: (Region | undefined)[];
  recentCountries: (ICountryFlagProps | undefined)[];
  saveLocation: (location: 'country' | 'region', id: string) => void;
  isOpusNotifierOn?: boolean;
}

export const itemToString = (item: ILabelValuePair<string>): string =>
  item?.value;

const CreateSubscription: React.FC<ICreateSubscriptionProps> = (props) => {
  const {
    subscriptions,
    customers,
    countries,
    entityId,
    setIsAddingNewNotification,
    isAddingNewNotification,
    loading,
    regions,
    setIsShowingNotifierBanner,
    isShowingNotifierBanner,
    setIsCreatingMobileAlert,
    recentCountries,
    recentRegions,
    saveLocation,
    isOpusNotifierOn,
  } = props;

  const initialCountry = { label: 'Global', value: null };
  const initialCustomer = { label: 'All DSPs', value: null };
  const initialRegion = { label: 'All Regions', value: null, countries: [] };
  const defaultCustomer = {
    thresholds: initialCustomer,
    charts: initialCustomer,
    playlisting: initialCustomer,
    priority: initialCustomer,
  };
  const subscription = createNewSubscription(entityId);
  const playlistCustomers = [
    initialCustomer,
    { label: 'Spotify', value: 1, name: 'spotify' },
  ];
  const isLargeScreen = useMedia('(min-width: 47rem)');
  const [selectedCountry, setSelectedCountry] = useState<any>({
    thresholds: initialCountry,
    charts: initialCountry,
    playlisting: initialCountry,
    priority: initialCountry,
  });
  const [selectedCustomer, setSelectedCustomer] =
    useState<any>(defaultCustomer);
  const [selectedRegion, setSelectedRegion] = useState<any>({
    thresholds: initialRegion,
    charts: initialRegion,
    playlisting: initialRegion,
    priority: initialRegion,
  });

  const [hasNoSelectedCheckboxes, setHasNoSelectedCheckboxes] = useState(false);
  const [enabledCategories, setEnabledCategories] = useState<
    Record<EventCategories, boolean>
  >({
    thresholds: false,
    charts: false,
    playlisting: false,
    priority: false,
  });

  const [currentSettings, save, cancel, , setCurrentSettings] = useSettings(
    subscription,
    entityId
  );

  const [handleChecked, getIsSelected] = useCheckboxes(
    currentSettings,
    setCurrentSettings
  );

  const handleCountryChange = useCallback(
    (country: any, notificationType: string) => {
      setCurrentSettings({
        ...currentSettings,
        countryId: country.value,
        regionId: {},
      });
      setSelectedCountry({ ...selectedCountry, [notificationType]: country });
      setSelectedRegion({
        ...selectedRegion,
        [notificationType]: initialRegion,
      });
    },
    [
      currentSettings,
      initialRegion,
      setCurrentSettings,
      setSelectedRegion,
      selectedCountry,
      selectedRegion,
    ]
  );

  const handleCustomerChange = useCallback(
    (customer: any, notificationType: string) => {
      setCurrentSettings({ ...currentSettings, customerId: customer.value });
      setSelectedCustomer({
        ...selectedCustomer,
        [notificationType]: customer,
      });
    },
    [currentSettings, setCurrentSettings, selectedCustomer]
  );

  const handleRegionChange = useCallback(
    (region: any, notificationType: string) => {
      setCurrentSettings({
        ...currentSettings,
        regionId: {
          ...currentSettings.regionId,
          [notificationType]: region.value,
        },
        countryId: undefined,
      });
      setSelectedRegion({ ...selectedRegion, [notificationType]: region });
      setSelectedCountry({
        ...selectedCountry,
        [notificationType]: initialCountry,
      });
    },
    [
      currentSettings,
      initialCountry,
      setCurrentSettings,
      setSelectedCountry,
      selectedRegion,
      selectedCountry,
    ]
  );

  const clearLocation = useCallback(
    (location: string, notificationType: string) => {
      if (location === 'region') {
        handleRegionChange(initialRegion, notificationType);
      } else {
        handleCountryChange(initialCountry, notificationType);
      }
    },
    [handleRegionChange, handleCountryChange, initialCountry, initialRegion]
  );

  const sortedCountries = useMemo(
    () => countries.sort(sortByDisplayName).map(addLabel),
    [countries]
  );

  const sortedCustomers = useMemo(() => {
    const sorted = customers.sort(sortByDisplayName).map(addLabel);
    return [initialCustomer, ...sorted];
  }, [initialCustomer, customers]);

  const sortedRegions = useMemo(
    () => regions.sort(sortByDisplayName).map(addLabel),
    [regions]
  );

  const handleCheckedInput =
    (eventCategory: EventCategories): any =>
    ({ value }: ILabelValuePair<ChannelTypes | 'all'>): void => {
      const alreadySelected = getIsSelected(eventCategory, value);
      const channels =
        eventCategory === 'priority' || eventCategory === 'charts'
          ? channelPriorityAndChartsTypeNames(eventCategory)
          : channelTypeNames;
      const channelsLength = Object.keys(channels).length;
      /** If we select 'Select all', we need to check/uncheck all the other items */
      if (value === 'all') {
        [...Object.keys(channels), 'all'].forEach((channel: ChannelTypes) => {
          handleChecked(channel, eventCategory, alreadySelected);
        });
        setHasNoSelectedCheckboxes(alreadySelected);
      } else {
        const selected = currentSettings.eventChannels
          .filter((channel: any) => channel.channelId !== 'all')
          .filter((channel: any) =>
            channel?.eventCategories?.includes(eventCategory)
          );
        /** If we select all the items, we need to check 'Select all' option */
        if (eventCategory === 'priority') {
          if (selected.length === 2 && !alreadySelected) {
            handleChecked('all' as ChannelTypes, eventCategory, false);
          }
          if (selected.length === 3) {
            handleChecked('all' as ChannelTypes, eventCategory, true);
          }
        } else {
          if (selected.length === channelsLength - 1 && !alreadySelected) {
            handleChecked('all' as ChannelTypes, eventCategory, false);
          }
          if (selected.length === channelsLength) {
            handleChecked('all' as ChannelTypes, eventCategory, true);
          }
        }
        handleChecked(value as ChannelTypes, eventCategory, alreadySelected);
        setHasNoSelectedCheckboxes(false);
      }
      if (currentSettings.eventChannels.length <= 0)
        setHasNoSelectedCheckboxes(true);
    };

  const addNewNotification = (e: any, isNext?: boolean): void => {
    if (currentSettings.eventChannels.length <= 0) {
      setHasNoSelectedCheckboxes(true);
    } else {
      setHasNoSelectedCheckboxes(false);
      const chosenArea: any = cloneDeep(selectedCountry);
      for (const key in selectedCountry) {
        if (
          selectedCountry[key].value !== null ||
          selectedRegion[key].value !== null
        ) {
          chosenArea[key] =
            selectedCountry[key].value !== null
              ? selectedCountry[key]
              : selectedRegion[key];
        }
      }
      const subscriptionsWithParsedPriority = subscriptions?.map(
        (subscription: ISubscription) => ({
          ...subscription,
          eventChannels: parsePriorityTypes(subscription),
        })
      );

      const isMobileAlert = currentSettings?.eventChannels?.filter(
        (event: any) => event.channelId === 'push'
      );
      setIsCreatingMobileAlert(isMobileAlert);
      if (isMobileAlert?.length && isOpusNotifierOn) {
        setIsShowingNotifierBanner(true);
        save(subscriptionsWithParsedPriority, selectedCustomer, chosenArea);
        if (!isNext) {
          return;
        }
      }
      save(subscriptionsWithParsedPriority, selectedCustomer, chosenArea);
      setIsCreatingMobileAlert(false);
      setIsAddingNewNotification(false);
      setIsShowingNotifierBanner(false);
    }
  };

  const switchEventCategory = (eventCategory: EventCategories): void => {
    // enable/disable categories
    setEnabledCategories({
      ...enabledCategories,
      [`${eventCategory}`]: !enabledCategories[eventCategory],
    });

    // reset DSP and country, if category is unchecked
    if (enabledCategories[eventCategory]) {
      setSelectedCustomer(defaultCustomer);
      setSelectedCountry({
        ...selectedCountry,
        [eventCategory]: initialCountry,
      });
      setSelectedRegion({ ...selectedRegion, [eventCategory]: initialRegion });
    }

    // if we have chosen events and uncheck the category, we need to clear location, DSP and events for this category
    const eventChannels = currentSettings.eventChannels.reduce(
      (prev: IEventChannel[], current: IEventChannel) =>
        current.eventCategories.includes(eventCategory)
          ? [
              ...prev,
              {
                channelId: current.channelId,
                eventCategories: current.eventCategories.filter(
                  (category: EventCategories) => category !== eventCategory
                ),
              },
            ]
          : [...prev, current],
      []
    );

    setCurrentSettings({ ...currentSettings, eventChannels });
  };

  const getItems = (): { label: string; value: string }[] => {
    if (SHOW_BELL_NOTIFICATION) {
      return [
        { label: 'Select all', value: 'all' },
        { label: 'OPUS website notifications', value: 'on_site' },
        { label: 'Daily e-mail', value: 'email_daily_digest' },
        { label: 'Weekly e-mail', value: 'email_weekly_digest' },
      ];
    } else {
      return [
        { label: 'Select all', value: 'all' },
        { label: 'Daily e-mail', value: 'email_daily_digest' },
        { label: 'Weekly e-mail', value: 'email_weekly_digest' },
      ];
    }
  };
  const items = getItems();

  const itemsPriorityPlaylistAndCharts = (
    event: 'priority' | 'charts'
  ): any => {
    if (SHOW_BELL_NOTIFICATION) {
      return [
        { label: 'Select all', value: 'all' },
        { label: 'OPUS website notifications', value: 'on_site' },
        { label: 'E-mail immediately', value: 'standalone_email' },
        ...(event === 'priority' && isOpusNotifierOn
          ? [{ label: 'Mobile alert immediately', value: 'push' }]
          : []),
      ];
    } else {
      return [
        { label: 'Select all', value: 'all' },
        { label: 'E-mail immediately', value: 'standalone_email' },
      ];
    }
  };

  const onClickNext = (): void => addNewNotification(null, true);

  return (
    <>
      <Box
        mx={0}
        my={isLargeScreen ? '2em' : 6}
        py="28px"
        px="36px"
        bg="greys.6"
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        {isShowingNotifierBanner ? (
          <NotifierBanner />
        ) : (
          <>
            <Box width="100%">
              {Object.keys(eventCategoryLabels).map(
                (
                  event: EventCategories,
                  index: number,
                  arr: EventCategories[]
                ) => {
                  const isPlaylisting =
                    event === 'playlisting' || event === 'priority';
                  const selectedChannels = getSelectedEventCategory({
                    eventCategory: event,
                    currentSettings,
                    channelTypeNames:
                      event === 'priority' || event === 'charts'
                        ? channelPriorityAndChartsTypeNames(event)
                        : channelTypeNames,
                  }).filter((channel: any) =>
                    event === 'priority'
                      ? channel.label !== 'In a weekly e-mail'
                      : true
                  );
                  const eventItems =
                    event === 'priority' || event === 'charts'
                      ? itemsPriorityPlaylistAndCharts(event)
                      : items;
                  const showErrorBorder =
                    selectedChannels &&
                    selectedChannels.length === 0 &&
                    hasNoSelectedCheckboxes;

                  return (
                    <Box
                      mb="2em"
                      ml={isLargeScreen ? 0 : '-13px'}
                      pb={!isLargeScreen ? '30px' : 0}
                      borderBottom={
                        !isLargeScreen && index !== arr.length - 1
                          ? '1px solid #e9e9e9'
                          : ''
                      }
                      key={index}
                    >
                      <Checkbox
                        text={eventCategoryLabels[event]}
                        isChecked={enabledCategories[event]}
                        onClick={(): void => switchEventCategory(event)}
                      />

                      <P
                        my={0}
                        pl="30px"
                        color={(colors as any).BrownishGrey}
                        fontSize="13px"
                      >
                        {eventCategoryTooltipTexts[event]}
                      </P>

                      {enabledCategories[event] && (
                        <Box
                          display="grid"
                          gridGap={isLargeScreen ? 1 : isPlaylisting ? 2 : 3}
                          gridTemplateColumns={
                            !isLargeScreen
                              ? '1fr'
                              : isPlaylisting
                              ? '190px auto'
                              : '190px 190px auto'
                          }
                          alignItems="end"
                          ml={0}
                          mt={
                            isLargeScreen
                              ? showErrorBorder
                                ? 0
                                : '1.625em'
                              : 4
                          }
                          pb={isLargeScreen ? '1rem' : 0}
                          pl={isLargeScreen && isPlaylisting ? '30px' : 0}
                        >
                          {event !== 'playlisting' && event !== 'priority' && (
                            <LocationDropdown
                              clearLocation={(location: string): any =>
                                clearLocation(location, event)
                              }
                              onChange={(e: any): void => {
                                const handleChange = e.id.includes('RE')
                                  ? handleRegionChange
                                  : handleCountryChange;
                                handleChange(e, event);
                              }}
                              sortedCountries={sortedCountries}
                              sortedRegions={sortedRegions}
                              isLargeScreen={isLargeScreen}
                              recentCountries={recentCountries}
                              recentRegions={recentRegions}
                              saveLocation={saveLocation}
                            />
                          )}

                          <Box
                            height="34px"
                            marginBottom={isLargeScreen ? '' : '14px'}
                          >
                            <CustomerDropdown
                              onChange={(e): any =>
                                handleCustomerChange(e, event)
                              }
                              items={
                                isPlaylisting
                                  ? playlistCustomers
                                  : sortedCustomers
                              }
                              selectedItem={selectedCustomer[event]}
                              isSubscriptionForm
                            />
                          </Box>

                          <Box>
                            {selectedChannels.length === 0 &&
                              hasNoSelectedCheckboxes && (
                                <Span color="#FF0000" fontSize="0.8175em">
                                  Choose one
                                </Span>
                              )}

                            <Box
                              border={
                                showErrorBorder ? '1px solid #FF0000' : ''
                              }
                              height={showErrorBorder ? 'auto' : '34px'}
                            >
                              <ChannelDropdown
                                defaultLabel="Notify me by..."
                                items={eventItems}
                                itemToString={itemToString}
                                onChange={handleCheckedInput(event)}
                                selectedItems={selectedChannels}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                }
              )}

              <Box
                display="flex"
                alignItems="center"
                width={isLargeScreen ? 'auto' : '100%'}
              >
                <Button
                  data-pw="notifications-modal-add"
                  data-gtag={'notifications-modal-add'}
                  variant={loading ? 'disabled' : 'primary'}
                  onClick={addNewNotification}
                  type="button"
                  fontSize="16px"
                  fontWeight="600"
                  py={2}
                  px={5}
                  my={3}
                  width={!isLargeScreen ? '100%' : 'auto'}
                >
                  Add notification
                </Button>

                {isAddingNewNotification && (
                  <Button
                    ml={isLargeScreen ? 2 : 0}
                    variant="secondary"
                    onClick={(): void => {
                      cancel();
                      setIsAddingNewNotification(false);
                    }}
                    type="button"
                    height="41px"
                    width={isLargeScreen ? 'auto' : '100%'}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Box>

            {isAddingNewNotification && (
              <Box py={2} display="flex" justifyContent="flex-end">
                <CloseButton
                  type="button"
                  onClick={(): void => setIsAddingNewNotification(false)}
                >
                  <Close />
                </CloseButton>
              </Box>
            )}
          </>
        )}
      </Box>
      {isShowingNotifierBanner && (
        <ButtonNext
          display="flex"
          alignItems="center"
          margin="-16px 0 0 auto"
          px={0}
          onClick={onClickNext}
        >
          <Span lineHeight="0.75em" marginRight="6px">
            Next
          </Span>
          <Chevron />
        </ButtonNext>
      )}
    </>
  );
};

export default CreateSubscription;
